/** @jsxImportSource ./simboluri-jsx */
// DESENELE LAMELEI (Pasul 14, `armare_lamele.py`): secțiunea cotată t × L cu
// carcasele de capăt, etrierii lor, barele inimii pe două fețe, barele
// orizontale și agrafele; elevația stivei cu zonele A/B, l_cr și carcasele;
// și carcasa 3D a nivelului. Aceleași poziții de bară în toate trei — vin
// dintr-un singur loc, `bareLamela`, ca secțiunea și scena să nu se
// contrazică.
//
// Convenții: L pe orizontală (ca în plan), t pe verticală; barele
// longitudinale roșii, etrierii negri, barele orizontale verzi, agrafele
// violet — culorile secțiunii peretelui de subsol (`StepArmarePereti`).

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { t, useT } from "../i18n";
import type { ArmareLamela, StivaLamela } from "./tipuri";
import { CotaH, CotaV, HasuraPlaca, cmFmt, mFmt } from "./cote";
import { PAL } from "./paletaDesen";
import { incadreaza } from "./incadrare3d";

const COL = {
  beton: "#ececec",
  betonLinie: "#737373",
  bara: PAL.barSus,
  etrier: PAL.etrieri,
  orizontal: "#2e7d72",
  agrafe: PAL.agrafe,
  critica: "#f5ecd8",
  infra: "#e3e6ea",
  carcasa: "#f7e2e4",
  placa: "#dbdfe4",
  placaLinie: "#8f9298",
  acc: "#0b7f92",
};

/** Pozițiile barelor în secțiune, în cm: x de-a lungul lui L (0 = capătul
 *  stâng), y peste grosime (0 = fața de jos a desenului). Capetele au `n`
 *  bare fiecare, jumătate pe fiecare față, întinse pe lungimea carcasei;
 *  inima are `nr_bare` în total, tot pe două fețe, la pasul lor între cele
 *  două carcase. `lcf` e lungimea confinată (etrierul capătului o cuprinde;
 *  în zona critică e mai lungă decât carcasa, P100-1 §5.3.4.3.2(6)), iar
 *  `ramuri` sunt abscisele agrafelor de confinare ale capătului stâng —
 *  câte una pe rând de bare cuprins, fără rândul de la capăt, pe care îl
 *  ține etrierul (`agrafe_pe_set`); capătul drept le oglindește. */
export function bareLamela(l: ArmareLamela, cCm: number) {
  const L = l.L_cm, tt = l.t_cm;
  const a = l.armare;
  const cap = a.capete;
  const eF = cap.etrieri.phi_mm / 10;
  // bara stă în interiorul etrierului: acoperire + Ø etrier + jumătate de Ø
  const ins = cCm + eF + cap.phi_mm / 20;
  const fete = [ins, tt - ins];
  const lc = Math.min(cap.l_cm, L / 2);
  const lcf = Math.min(Math.max(cap.etrieri.l_confinat_cm ?? 0, lc), L / 2);
  const nFata = Math.max(1, Math.round(cap.n / 2));
  const xCap = Array.from({ length: nFata }, (_, i) =>
    nFata === 1 ? lc / 2 : ins + ((lc - 2 * ins) * i) / (nFata - 1));
  const capete: [number, number][] = [];
  for (const x of xCap) for (const y of fete) capete.push([x, y], [L - x, y]);
  const nInima = Math.max(0, Math.round(a.vertical.nr_bare / 2));
  const lInima = Math.max(L - 2 * lc, 0);
  const insV = cCm + a.orizontal.phi_mm / 10 + a.vertical.phi_mm / 20;
  const inima: [number, number][] = [];
  for (let k = 0; k < nInima && lInima > 0; k++) {
    const x = lc + (lInima * (k + 0.5)) / nInima;
    inima.push([x, insV], [x, tt - insV]);
  }
  const randuri = [...xCap.slice(1),
                   ...inima.map(([x]) => x).filter((x, i) => i % 2 === 0 && x < lcf - ins)];
  const k = cap.etrieri.agrafe_pe_set;
  const ramuri = k === undefined ? randuri : randuri.slice(0, Math.max(k, 0));
  return { capete, inima, lc, lcf, xCap, fete, lInima, ramuri };
}

// ---------------------------------------------------------------------------
// Secțiunea transversală cotată
// ---------------------------------------------------------------------------

export default function SectiuneLamela({ l, cCm }: { l: ArmareLamela; cCm: number }) {
  useT();
  const L = l.L_cm, tt = l.t_cm;
  const a = l.armare;
  const e = a.capete.etrieri;
  const sc = Math.min(3, 640 / Math.max(L, 1));
  const ox = 76, oy = 44;
  const W = ox + L * sc + 24;
  const H = oy + tt * sc + 108;
  const { capete, inima, lc, lcf, lInima, ramuri } = bareLamela(l, cCm);
  const X = (x: number) => ox + x * sc;
  const Y = (y: number) => oy + y * sc;
  const rCap = Math.max(1.8, (a.capete.phi_mm / 20) * sc);
  const rIn = Math.max(1.4, (a.vertical.phi_mm / 20) * sc);
  // pasul barelor inimii, cotat între două bare vecine de pe aceeași față
  const pasV = a.vertical.pas_mm / 10;
  const insE = Math.max(cCm - e.phi_mm / 20, 0.5);
  // agrafele leagă cele două plase, pe o rețea de ~1/√(nr pe m²), pe inima
  // dintre zonele confinate
  const pasAgr = a.agrafe.per_m2 > 0 ? 100 / Math.sqrt(a.agrafe.per_m2) : 0;
  const agrafe: number[] = [];
  if (pasAgr > 0) for (let x = lcf + pasAgr / 2; x < L - lcf; x += pasAgr) agrafe.push(x);
  const yText = Y(tt) + 44;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} data-testid="sectiune-lamela"
         style={{ width: "100%", maxWidth: W, background: "#fff",
                  border: "1px solid #dedede", borderRadius: 6 }}>
      <text x={4} y={14} fontSize={12} fill="#555">
        {t("struct.lam.sect_titlu", { id: l.element, t: cmFmt(tt), L: cmFmt(L) })}
      </text>
      <rect x={X(0)} y={Y(0)} width={L * sc} height={tt * sc}
            fill={COL.beton} stroke={COL.betonLinie} />
      {/* capetele: etrierul închis pe zona confinată + agrafele de
          confinare pe rândurile de bare cuprinse */}
      {[0, L - lcf].map((x0) => (
        <g key={`e${x0}`}>
          <rect x={X(x0 + insE)} y={Y(insE)} width={(lcf - 2 * insE) * sc}
                height={(tt - 2 * insE) * sc} rx={3}
                fill="none" stroke={COL.etrier} strokeWidth={1.3} />
          {ramuri.map((xc, i) => (
            <line key={i} x1={X(x0 === 0 ? xc : L - xc)} y1={Y(insE)}
                  x2={X(x0 === 0 ? xc : L - xc)} y2={Y(tt - insE)}
                  stroke={COL.etrier} strokeWidth={0.9} />
          ))}
        </g>
      ))}
      {/* barele orizontale: pe fiecare față, între carcase */}
      {lInima > 0 && [cCm + a.orizontal.phi_mm / 20, tt - cCm - a.orizontal.phi_mm / 20]
        .map((y, i) => (
          <line key={`o${i}`} x1={X(lc)} y1={Y(y)} x2={X(L - lc)} y2={Y(y)}
                stroke={COL.orizontal}
                strokeWidth={Math.max(1, (a.orizontal.phi_mm / 10) * sc)} />
        ))}
      {/* agrafele */}
      {agrafe.map((x, i) => (
        <line key={`a${i}`} x1={X(x)} y1={Y(cCm)} x2={X(x)} y2={Y(tt - cCm)}
              stroke={COL.agrafe} strokeWidth={1.1} />
      ))}
      {capete.map(([x, y], i) => (
        <circle key={`c${i}`} cx={X(x)} cy={Y(y)} r={rCap} fill={COL.bara} />
      ))}
      {inima.map(([x, y], i) => (
        <circle key={`i${i}`} cx={X(x)} cy={Y(y)} r={rIn} fill={COL.bara} />
      ))}
      {/* COTELE: L dedesubt, t și c la stânga, carcasa și pasul inimii
          deasupra */}
      <CotaH x1={X(0)} x2={X(L)} y={Y(tt) + 22} martorY={Y(tt)}
             text={`L = ${cmFmt(L)} cm`} />
      <CotaV x={ox - 24} y1={Y(0)} y2={Y(tt)} martorX={X(0)}
             text={`t = ${cmFmt(tt)} cm`} />
      <CotaV x={ox - 54} y1={Y(0)} y2={Y(cCm)} martorX={X(0)}
             text={`c = ${cmFmt(cCm)}`} size={9} />
      <CotaH x1={X(0)} x2={X(lc)} y={oy - 12} martorY={Y(0)} deasupra
             text={`l_c = ${cmFmt(lc)}`} size={9} culoare={COL.bara} />
      {inima.length >= 4 && (
        <CotaH x1={X(inima[0][0])} x2={X(inima[2][0])} y={oy - 12} martorY={Y(0)}
               deasupra text={`s = ${mFmt(pasV, 1)}`} size={9} culoare={COL.bara} />
      )}
      <text x={ox} y={yText} fontSize={11} fill={COL.bara}>
        {t("struct.lam.leg_capete", { n: a.capete.n, phi: a.capete.phi_mm,
                                      l: cmFmt(lc) })}
      </text>
      <text x={ox} y={yText + 15} fontSize={11} fill={COL.etrier}>
        {t("struct.lam.leg_etrieri", { phi: e.phi_mm, pas: mFmt(e.pas_cm, 1),
                                       a: mFmt(e.ochi_cm?.[0], 1), b: mFmt(e.ochi_cm?.[1], 1),
                                       lconf: cmFmt(e.l_confinat_cm) })}
      </text>
      <text x={ox} y={yText + 30} fontSize={11} fill={COL.bara}>
        {t("struct.lam.leg_inima", { n: a.vertical.nr_bare, phi: a.vertical.phi_mm,
                                     pas: a.vertical.pas_mm })}
      </text>
      <text x={ox} y={yText + 45} fontSize={11} fill={COL.orizontal}>
        {t("struct.lam.leg_oriz", { phi: a.orizontal.phi_mm, pas: a.orizontal.pas_mm,
                                    phia: a.agrafe.phi_mm, na: a.agrafe.per_m2 })}
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Elevația stivei: nivelurile unul peste altul, zonele A/B, l_cr, carcasele
// ---------------------------------------------------------------------------

export function VedereLamelaCotata({ nivele, selectat, stiva, cCm, latime = 560,
                                     inaltimeMax = 520 }: {
  /** nivelurile stivei, în orice ordine (se așază după `etaj`) */
  nivele: ArmareLamela[];
  /** id-ul nivelului evidențiat („ST13.3") */
  selectat?: string;
  stiva?: StivaLamela | null;
  cCm: number;
  latime?: number;
  inaltimeMax?: number;
}) {
  useT();
  const niv = [...nivele].sort((a, b) => a.etaj - b.etaj);
  if (!niv.length) return null;
  const L = Math.max(...niv.map((x) => x.L_cm)) / 100;
  const total = niv.reduce((s, x) => s + x.H_m, 0);
  const padT = 34, padB = 44;
  const pxm = Math.max(9, Math.min(60, (inaltimeMax - padT - padB) / Math.max(total, 0.5)));
  const H = Math.round(total * pxm) + padT + padB;
  const W = latime;
  const ax = Math.round(W * 0.42);
  const Y = (m: number) => padT + (total - m) * pxm;
  const X = (m: number) => ax + m * pxm;
  const hPl = 0.15;
  const ext = Math.min(0.5, (W * 0.08) / pxm);
  const zonaA = new Set(stiva?.zona_A ?? []);
  const infra = new Set(stiva?.infrastructura ?? []);
  const corp: React.ReactNode[] = [];
  const armat: React.ReactNode[] = [];
  const cote: React.ReactNode[] = [];
  const etich: React.ReactNode[] = [];
  let z = 0;
  let zBaza: number | null = null;
  niv.forEach((n, i) => {
    const z0 = z, z1 = z + n.H_m;
    z = z1;
    const Ln = n.L_cm / 100;
    const key = `n${i}`;
    if (stiva?.baza && n.element === stiva.baza) zBaza = z0;
    const fill = infra.has(n.element) ? COL.infra : zonaA.has(n.element) ? COL.critica : COL.beton;
    const ales = n.element === selectat;
    corp.push(
      <rect key={`${key}c`} x={X(-Ln / 2)} y={Y(z1)} width={Ln * pxm}
            height={(z1 - z0) * pxm} fill={fill}
            stroke={ales ? COL.acc : COL.betonLinie} strokeWidth={ales ? 1.8 : 1} />
    );
    // placa de la capul nivelului, ca reper al planșeelor
    corp.push(
      <rect key={`${key}p`} x={X(-Ln / 2 - ext)} y={Y(z1)} width={(Ln + 2 * ext) * pxm}
            height={hPl * pxm} fill={COL.placa} stroke={COL.placaLinie} strokeWidth={0.8} />
    );
    corp.push(
      <HasuraPlaca key={`${key}h`} x={X(-Ln / 2 - ext)} y={Y(z1)}
                   w={(Ln + 2 * ext) * pxm} h={hPl * pxm} />
    );
    // carcasele de capăt + etrierii lor la pasul din zonă
    const { lc, inima } = bareLamela(n, cCm);
    const lcM = lc / 100;
    for (const x0 of [-Ln / 2, Ln / 2 - lcM]) {
      armat.push(
        <rect key={`${key}k${x0}`} x={X(x0)} y={Y(z1 - hPl)} width={lcM * pxm}
              height={(z1 - hPl - z0) * pxm} fill={COL.carcasa} stroke={COL.bara}
              strokeWidth={0.8} />
      );
      const pasE = n.armare.capete.etrieri.pas_cm / 100;
      if (pasE * pxm >= 3) {
        const linii: string[] = [];
        for (let ze = z0 + 0.05; ze < z1 - hPl; ze += pasE)
          linii.push(`M ${X(x0 + cCm / 100)},${Y(ze)} L ${X(x0 + lcM - cCm / 100)},${Y(ze)}`);
        armat.push(
          <path key={`${key}e${x0}`} d={linii.join(" ")} fill="none"
                stroke={COL.etrier} strokeWidth={0.6} />
        );
      }
    }
    // barele inimii, câte una pe poziție (cele două fețe se suprapun în elevație)
    const xInima = [...new Set(inima.map(([x]) => x))];
    if (xInima.length > 1 && (xInima[1] - xInima[0]) / 100 * pxm >= 4) {
      armat.push(
        <path key={`${key}v`}
              d={xInima.map((x) => `M ${X(-Ln / 2 + x / 100)},${Y(z0)} L ${X(-Ln / 2 + x / 100)},${Y(z1 - hPl)}`).join(" ")}
              fill="none" stroke={COL.bara} strokeWidth={0.7} />
      );
    }
    if ((z1 - z0) * pxm >= 40) {
      cote.push(
        <CotaV key={`${key}H`} x={X(-L / 2) - 24} y1={Y(z1)} y2={Y(z0)}
               martorX={X(-Ln / 2)} text={`H ${mFmt(n.H_m)}`} />
      );
    }
    if ((z1 - z0) * pxm >= 22) {
      etich.push(
        <text key={`${key}t`} x={X(L / 2 + ext) + 10} y={Y((z0 + z1) / 2) + 3}
              fontFamily="var(--mono)" fontSize={9.5}
              fill={ales ? COL.acc : "var(--mut)"}>
          {n.element} · {n.zona === "infrastructura" ? "I" : n.zona} · 2×{n.armare.capete.n}Ø{n.armare.capete.phi_mm} · Ø{n.armare.vertical.phi_mm}/{n.armare.vertical.pas_mm}
        </text>
      );
    }
  });
  // zona critică de la baza peretelui: l_cr de la nivelul de bază în sus
  if (stiva?.l_cr_m && zBaza != null) {
    const zb: number = zBaza;
    cote.push(
      <CotaV key="lcr" x={X(-L / 2) - 62} y1={Y(zb + stiva.l_cr_m)} y2={Y(zb)}
             martorX={X(-L / 2)} text={`l_cr ${mFmt(stiva.l_cr_m)}`} culoare={COL.acc} />
    );
  }
  cote.push(
    <CotaH key="L" x1={X(-L / 2)} x2={X(L / 2)} y={Y(0) + 26} martorY={Y(0)}
           text={`L = ${mFmt(L)} m`} />
  );
  return (
    <svg viewBox={`0 0 ${W} ${H}`} data-testid="vedere-lamela"
         style={{ width: "100%", maxWidth: W, background: "#fff",
                  border: "1px solid #dedede", borderRadius: 6 }}>
      <text x={4} y={14} fontSize={12} fill="#555">
        {t("struct.lam.vedere_titlu", { stack: niv[0].stack, L: mFmt(L),
                                        t: cmFmt(niv[0].t_cm) })}
      </text>
      {corp}
      {armat}
      {cote}
      {etich}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Carcasa 3D a nivelului
// ---------------------------------------------------------------------------

export function Lamela3D({ l, cCm, height = 420 }: {
  l: ArmareLamela;
  cCm: number;
  height?: number;
}) {
  const mount = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = mount.current;
    if (!el) return;
    const W = el.clientWidth || 900, Hpx = height;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3f6f9);
    const camera = new THREE.PerspectiveCamera(40, W / Hpx, 0.01, 200);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, Hpx);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    el.appendChild(renderer.domElement);

    const L = l.L_cm / 100, tt = l.t_cm / 100, Hn = l.H_m;
    const gri = (op: number, col = 0x9aa0a8) =>
      new THREE.MeshStandardMaterial({ color: col, transparent: true, opacity: op, depthWrite: false });
    const box = new THREE.Mesh(new THREE.BoxGeometry(L, tt, Hn), gri(0.16));
    box.position.set(0, 0, Hn / 2);
    scene.add(box);
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(L, tt, Hn)),
      new THREE.LineBasicMaterial({ color: 0x777777 }));
    edges.position.set(0, 0, Hn / 2);
    scene.add(edges);

    const { capete, inima, lcf, ramuri } = bareLamela(l, cCm);
    const bara = (x: number, y: number, phi: number) => {
      const geo = new THREE.CylinderGeometry(phi / 2000, phi / 2000, Hn, 8);
      const m = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0xc0392b }));
      m.rotation.x = Math.PI / 2;
      m.position.set(x / 100 - L / 2, y / 100 - tt / 2, Hn / 2);
      scene.add(m);
    };
    for (const [x, y] of capete) bara(x, y, l.armare.capete.phi_mm);
    for (const [x, y] of inima) bara(x, y, l.armare.vertical.phi_mm);

    // etrierii capetelor: ochiul închis pe zona confinată + agrafele de
    // confinare pe rândurile de bare cuprinse, la pasul zonei
    const e = l.armare.capete.etrieri;
    const insE = Math.max(cCm - e.phi_mm / 20, 0.5) / 100;
    const pts: number[] = [];
    const lcM = lcf / 100;
    for (const x0 of [-L / 2, L / 2 - lcM]) {
      const xa = x0 + insE, xb = x0 + lcM - insE;
      const ya = -tt / 2 + insE, yb = tt / 2 - insE;
      const xr_ = ramuri.map((xc) => x0 + (x0 < 0 ? xc : lcf - xc) / 100);
      for (let z = 0.04; z < Hn - 0.03; z += Math.max(e.pas_cm, 3) / 100) {
        pts.push(xa, ya, z, xb, ya, z, xb, ya, z, xb, yb, z,
                 xb, yb, z, xa, yb, z, xa, yb, z, xa, ya, z);
        for (const xr of xr_) pts.push(xr, ya, z, xr, yb, z);
      }
    }
    const geoE = new THREE.BufferGeometry();
    geoE.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    scene.add(new THREE.LineSegments(geoE, new THREE.LineBasicMaterial({ color: 0x232323 })));

    // barele orizontale ale inimii, pe ambele fețe
    const o = l.armare.orizontal;
    const yO = tt / 2 - cCm / 100 - o.phi_mm / 2000;
    const ptsO: number[] = [];
    for (let z = 0.06; z < Hn - 0.03; z += Math.max(o.pas_mm, 50) / 1000)
      for (const y of [-yO, yO]) ptsO.push(-L / 2 + lcM, y, z, L / 2 - lcM, y, z);
    const geoO = new THREE.BufferGeometry();
    geoO.setAttribute("position", new THREE.Float32BufferAttribute(ptsO, 3));
    scene.add(new THREE.LineSegments(geoO, new THREE.LineBasicMaterial({ color: 0x2e7d72 })));

    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dl = new THREE.DirectionalLight(0xffffff, 0.7);
    dl.position.set(3, -4, 6);
    scene.add(dl);
    camera.position.set(L * 0.6 + 1.4, -(L * 0.8 + 1.8), Hn * 0.8);
    camera.up.set(0, 0, 1);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, Hn / 2);
    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);
    incadreaza(camera, controls, scene);
    render();
    return () => {
      controls.removeEventListener("change", render);
      controls.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [l, cCm, height]);
  return <div ref={mount} style={{ width: "100%", height }} />;
}
